import Swiper from "swiper";
import type { SwiperOptions } from "swiper/types";
import { onWindowResize, removeCallback } from "~/foundation/Events/onWindowResize";
import { refreshOverviewSlideGraphIds } from "./decade-hover-graph-animations";
import {
	MOBILE_OVERVIEW_CENTER_SPEED_MS,
	MOBILE_OVERVIEW_EXPAND_DELAY_MS,
	MOBILE_OVERVIEW_EXPAND_DURATION_MS,
	PAGE_MARGIN_MD_REM,
	PAGE_MARGIN_SM_REM,
	TIMELINE_DESKTOP_MIN_WIDTH_PX,
} from "./timeline-constants";

export function isMobileOverview(): boolean {
	return window.innerWidth < TIMELINE_DESKTOP_MIN_WIDTH_PX;
}

export type OverviewMobileSwiperOptions = {
	root: HTMLElement;
	moduleRoot: HTMLElement;
	contentHost: HTMLElement;
	overviewHost: HTMLElement;
	columnElements: HTMLElement[];
	onDeactivate: () => void;
};

export type OverviewMobileSwiperController = {
	destroy: () => void;
	collapseExpanded: () => void;
	prepareForOverviewReturn: () => void;
	finishOverviewReturn: () => void;
	resetExpanded: () => void;
	centerDecade: (index: number, onComplete: () => void) => void;
	slideToIndex: (index: number) => void;
	syncLoopClones: () => void;
};

export function bindOverviewMobileSwiper(options: OverviewMobileSwiperOptions): OverviewMobileSwiperController {
	const {
		root,
		moduleRoot,
		contentHost,
		overviewHost,
		columnElements,
		onDeactivate,
	} = options;

	let swiper: Swiper | null = null;
	let swiperEl: HTMLElement | null = null;
	let swiperWrapper: HTMLElement | null = null;
	let originalSlideElements: HTMLElement[] = [];
	let isWrapped = false;
	let ignoreSlideChange = false;
	let pendingActivate: { index: number; callback: () => void } | null = null;
	let expandDelayTimer: ReturnType<typeof setTimeout> | null = null;
	let centerFallbackTimer: ReturnType<typeof setTimeout> | null = null;
	let expandCompleteTimer: ReturnType<typeof setTimeout> | null = null;
	let expandResizeObserver: ResizeObserver | null = null;
	let expandedSlideIndex: number | null = null;
	let isContentReparented = false;
	let contentRestoreParent: HTMLElement | null = null;
	let contentRestoreNextSibling: Node | null = null;

	const divider = overviewHost.querySelector<HTMLElement>(".historical-timeline__content-divider");
	const originalSlideCount = columnElements.length;

	const remToPx = (rem: number): number => {
		const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
		return rem * rootFontSize;
	};

	const getContentWrapperSlideOffsetPx = (): number => {
		// Matches `.content-wrapper` padding in content-wrapper.scss.
		const marginRem =
			window.innerWidth >= TIMELINE_DESKTOP_MIN_WIDTH_PX ? PAGE_MARGIN_MD_REM : PAGE_MARGIN_SM_REM;
		return remToPx(marginRem);
	};

	const getContentWrapperInlinePadding = (): { before: number; after: number } => {
		// Expanded slide width already includes page gutters via CSS; Swiper offsets would double them.
		if (expandedSlideIndex !== null) {
			return { before: 0, after: 0 };
		}

		const offset = getContentWrapperSlideOffsetPx();
		return { before: offset, after: offset };
	};

	const getExpandedSlideGutter = (): number => getContentWrapperSlideOffsetPx() * 2;

	const getActiveDecadeIndex = (instance: Swiper): number => instance.activeIndex;

	const refreshSlideGraphs = () => {
		if (swiperWrapper) {
			refreshOverviewSlideGraphIds(swiperWrapper);
		}
	};

	const syncSlideOffsets = (
		instance: Swiper | null = swiper,
		options: { recenterIndex?: number; speed?: number } = {}
	) => {
		if (!instance) {
			return;
		}

		const { before, after } = getContentWrapperInlinePadding();
		const gutter = getExpandedSlideGutter();
		const { recenterIndex, speed = 0 } = options;

		moduleRoot.style.setProperty("--mobile-overview-slide-gutter", `${gutter}px`);

		const offsetsChanged =
			(instance.params.slidesOffsetBefore ?? 0) !== before ||
			(instance.params.slidesOffsetAfter ?? 0) !== after;

		if (offsetsChanged) {
			instance.params.slidesOffsetBefore = before;
			instance.params.slidesOffsetAfter = after;
		}

		if (!offsetsChanged && recenterIndex === undefined) {
			return;
		}

		instance.update();

		if (recenterIndex !== undefined) {
			instance.slideTo(recenterIndex, speed, false);
		}
	};

	const clearSelectionTimers = () => {
		if (expandDelayTimer) {
			clearTimeout(expandDelayTimer);
			expandDelayTimer = null;
		}

		if (centerFallbackTimer) {
			clearTimeout(centerFallbackTimer);
			centerFallbackTimer = null;
		}

		if (expandCompleteTimer) {
			clearTimeout(expandCompleteTimer);
			expandCompleteTimer = null;
		}
	};

	const stopExpandResizeObserver = () => {
		expandResizeObserver?.disconnect();
		expandResizeObserver = null;
	};

	const setExpandedSlide = (index: number | null) => {
		expandedSlideIndex = index;
		originalSlideElements.forEach((slide, slideIndex) => {
			slide.classList.toggle("is-expanded", slideIndex === index);
		});
	};

	const clearExpandedSlide = () => {
		setExpandedSlide(null);
	};

	const recenterActiveSlide = (index: number, speed = MOBILE_OVERVIEW_CENTER_SPEED_MS) => {
		if (!swiper || index < 0 || index >= originalSlideCount) {
			return;
		}

		ignoreSlideChange = true;
		syncSlideOffsets(swiper, { recenterIndex: index, speed });

		if (speed === 0) {
			requestAnimationFrame(() => {
				ignoreSlideChange = false;
			});
			return;
		}

		window.setTimeout(() => {
			ignoreSlideChange = false;
		}, speed + 50);
	};

	const startExpandResizeObserver = (slide: HTMLElement, index: number) => {
		stopExpandResizeObserver();

		expandResizeObserver = new ResizeObserver(() => {
			if (!slide.classList.contains("is-expanded")) {
				return;
			}

			recenterActiveSlide(index, 0);
		});

		expandResizeObserver.observe(slide);
	};

	const completeExpand = (index: number, callback: () => void) => {
		stopExpandResizeObserver();
		clearSelectionTimers();
		callback();
		pendingActivate = null;
		ignoreSlideChange = false;

		requestAnimationFrame(() => {
			recenterActiveSlide(index, 0);
		});
	};

	const waitForExpandComplete = (index: number, callback: () => void) => {
		const slide = originalSlideElements[index];
		if (!slide) {
			completeExpand(index, callback);
			return;
		}

		let completed = false;

		const finish = () => {
			if (completed) {
				return;
			}

			completed = true;
			slide.removeEventListener("transitionend", onTransitionEnd);
			completeExpand(index, callback);
		};

		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.target !== slide || event.propertyName !== "width") {
				return;
			}

			if (!slide.classList.contains("is-expanded")) {
				return;
			}

			finish();
		};

		slide.addEventListener("transitionend", onTransitionEnd);
		expandCompleteTimer = setTimeout(finish, MOBILE_OVERVIEW_EXPAND_DURATION_MS + 80);
	};

	const scheduleExpand = () => {
		if (!swiper || !pendingActivate) {
			return;
		}

		const { index, callback } = pendingActivate;

		clearSelectionTimers();

		expandDelayTimer = setTimeout(() => {
			expandDelayTimer = null;

			if (!swiper || pendingActivate?.index !== index) {
				return;
			}

			const slide = originalSlideElements[index];
			if (!slide) {
				return;
			}

			setExpandedSlide(index);
			refreshSlideGraphs();
			syncSlideOffsets(swiper, { recenterIndex: index, speed: 0 });
			startExpandResizeObserver(slide, index);
			waitForExpandComplete(index, callback);
		}, MOBILE_OVERVIEW_EXPAND_DELAY_MS);
	};

	const bindSlideWidthRefresh = () => {
		originalSlideElements.forEach((slide, slideIndex) => {
			slide.addEventListener("transitionend", (event) => {
				if (event.target !== slide || event.propertyName !== "width") {
					return;
				}

				if (!swiper) {
					return;
				}

				syncSlideOffsets(swiper, { recenterIndex: slideIndex, speed: 0 });
			});
		});
	};

	const moveContentOutsideWrapper = () => {
		if (isContentReparented) {
			return;
		}

		const contentWrapper = root.closest<HTMLElement>(".content-wrapper");
		if (!contentWrapper) {
			return;
		}

		contentRestoreParent = contentHost.parentElement;
		contentRestoreNextSibling = contentHost.nextSibling;
		contentWrapper.insertAdjacentElement("afterend", contentHost);
		isContentReparented = true;
		moduleRoot.classList.add("is-mobile-overview-layout");
	};

	const restoreContentPlacement = () => {
		if (!isContentReparented || !contentRestoreParent) {
			return;
		}

		contentRestoreParent.insertBefore(contentHost, contentRestoreNextSibling);
		contentRestoreParent = null;
		contentRestoreNextSibling = null;
		isContentReparented = false;
		moduleRoot.classList.remove("is-mobile-overview-layout");
	};

	const slideToRealIndex = (index: number, speed = 0, onComplete?: () => void) => {
		if (!swiper || index < 0 || index >= originalSlideCount) {
			return;
		}

		ignoreSlideChange = true;

		let centerCompleted = false;

		const finishCenter = () => {
			if (centerCompleted || !swiper) {
				return;
			}

			centerCompleted = true;
			swiper.off("slideChangeTransitionEnd", handleTransitionEnd);

			if (centerFallbackTimer) {
				clearTimeout(centerFallbackTimer);
				centerFallbackTimer = null;
			}

			onComplete?.();
		};

		const handleTransitionEnd = () => {
			finishCenter();
		};

		if (speed > 0 && onComplete) {
			swiper.on("slideChangeTransitionEnd", handleTransitionEnd);
			centerFallbackTimer = setTimeout(finishCenter, speed + 80);
		}

		syncSlideOffsets(swiper, { recenterIndex: index, speed });

		if (speed === 0) {
			window.setTimeout(() => {
				ignoreSlideChange = false;
				onComplete?.();
			}, 50);
			return;
		}

		if (!onComplete) {
			window.setTimeout(() => {
				ignoreSlideChange = false;
			}, speed + 50);
		}
	};

	const collapseExpanded = () => {
		clearSelectionTimers();
		stopExpandResizeObserver();
		pendingActivate = null;
		clearExpandedSlide();
	};

	const prepareForOverviewReturn = () => {
		moduleRoot.classList.add("is-overview-returning");
		collapseExpanded();

		if (!swiper) {
			return;
		}

		ignoreSlideChange = true;
		syncSlideOffsets(swiper, { recenterIndex: 0, speed: 0 });
	};

	const finishOverviewReturn = () => {
		ignoreSlideChange = false;
		moduleRoot.classList.remove("is-overview-returning");
	};

	const resetExpanded = () => {
		collapseExpanded();
		onDeactivate();
		slideToRealIndex(0, 0);
	};

	const centerDecade = (index: number, onComplete: () => void) => {
		if (!swiper || index < 0 || index >= originalSlideCount) {
			return;
		}

		clearSelectionTimers();
		stopExpandResizeObserver();
		clearExpandedSlide();
		pendingActivate = { index, callback: onComplete };
		ignoreSlideChange = true;

		const proceedToExpand = () => {
			if (!swiper || pendingActivate?.index !== index) {
				return;
			}

			// Last slides often cannot become activeIndex (not enough scroll room when
			// centeredSlides is off). Expand the tapped decade anyway.
			scheduleExpand();
		};

		const isAlreadyActive = getActiveDecadeIndex(swiper) === index && !swiper.animating;

		if (isAlreadyActive) {
			proceedToExpand();
			return;
		}

		slideToRealIndex(index, MOBILE_OVERVIEW_CENTER_SPEED_MS, proceedToExpand);
	};

	const slideToIndex = (index: number) => {
		clearSelectionTimers();
		pendingActivate = null;
		slideToRealIndex(index);
	};

	const unwrapDom = () => {
		if (!isWrapped || !swiperEl) {
			return;
		}

		originalSlideElements.forEach((slide) => {
			const column = slide.firstElementChild;
			if (column) {
				overviewHost.insertBefore(column, divider);
			}
		});

		swiperEl.remove();
		swiperEl = null;
		swiperWrapper = null;
		originalSlideElements = [];
		isWrapped = false;
	};

	const destroySwiper = () => {
		clearSelectionTimers();
		stopExpandResizeObserver();
		if (swiper) {
			swiper.destroy(true, true);
			swiper = null;
		}
	};

	const wrapDom = () => {
		if (isWrapped || columnElements.length === 0) {
			return;
		}

		swiperEl = document.createElement("div");
		swiperEl.className = "swiper historical-timeline__overview-swiper";

		swiperWrapper = document.createElement("div");
		swiperWrapper.className = "swiper-wrapper";
		const wrapper = swiperWrapper;

		columnElements.forEach((column) => {
			const slide = document.createElement("div");
			slide.className = "swiper-slide historical-timeline__overview-slide";
			slide.appendChild(column);
			wrapper.appendChild(slide);
			originalSlideElements.push(slide);
		});

		swiperEl.appendChild(swiperWrapper);
		overviewHost.insertBefore(swiperEl, divider);
		isWrapped = true;

		bindSlideWidthRefresh();
	};

	const initSwiper = () => {
		if (!swiperEl || swiper) {
			return;
		}

		const { before, after } = getContentWrapperInlinePadding();

		const swiperConfig: SwiperOptions = {
			slidesPerView: "auto",
			slidesPerGroup: 1,
			slidesPerGroupAuto: false,
			centeredSlides: false,
			spaceBetween: 0,
			slideToClickedSlide: false,
			speed: MOBILE_OVERVIEW_CENTER_SPEED_MS,
			watchOverflow: true,
			loop: false,
			initialSlide: 0,
			slidesOffsetBefore: before,
			slidesOffsetAfter: after,
			runCallbacksOnInit: false,
			on: {
				init: (instance) => {
					syncSlideOffsets(instance, { recenterIndex: 0, speed: 0 });
					refreshSlideGraphs();
				},
				resize: (instance) => {
					const recenterIndex = expandedSlideIndex ?? pendingActivate?.index ?? instance.activeIndex;
					syncSlideOffsets(instance, { recenterIndex, speed: 0 });
					refreshSlideGraphs();
				},
				slideChange: (instance) => {
					if (ignoreSlideChange || pendingActivate) {
						return;
					}

					const activeIndex = getActiveDecadeIndex(instance);
					const activeColumn = columnElements[activeIndex];
					if (activeColumn?.classList.contains("is-hovered")) {
						return;
					}

					clearExpandedSlide();
					onDeactivate();
					syncSlideOffsets(instance, { recenterIndex: activeIndex, speed: 0 });
				},
			},
		};

		swiper = new Swiper(swiperEl, swiperConfig);

		requestAnimationFrame(() => {
			syncSlideOffsets(swiper, { recenterIndex: 0, speed: 0 });
			refreshSlideGraphs();
		});
	};

	const enableMobile = () => {
		root.classList.add("is-mobile-overview");
		moveContentOutsideWrapper();

		if (isWrapped) {
			const recenterIndex = swiper?.activeIndex ?? 0;
			syncSlideOffsets(swiper, { recenterIndex, speed: 0 });
			refreshSlideGraphs();
			return;
		}

		wrapDom();
		initSwiper();
	};

	const disableMobile = () => {
		root.classList.remove("is-mobile-overview");
		resetExpanded();
		destroySwiper();
		unwrapDom();
		restoreContentPlacement();
	};

	const handleResize = () => {
		if (isMobileOverview()) {
			enableMobile();
			return;
		}

		disableMobile();
	};

	handleResize();
	onWindowResize(handleResize);

	return {
		destroy: () => {
			removeCallback(handleResize);
			disableMobile();
		},
		collapseExpanded,
		prepareForOverviewReturn,
		finishOverviewReturn,
		resetExpanded,
		centerDecade,
		slideToIndex,
		syncLoopClones: refreshSlideGraphs,
	};
}
